import { Button, Image, Link, Text, VStack } from '@chakra-ui/react';
import { ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorImage from 'assets/subscription/error.webp';
import { ExternalRoutes } from 'types/Routes';
import { classifyError, type ClassifyErrorOptions } from 'utils/classifyError';

type Props = {
  error: unknown;
  onRetry?: () => void;
  showBack?: boolean;
  showSupportLink?: boolean;
  expectedCodeErrors?: string[];
  onAction?: () => void;
  actionLabel?: string;
  classifyOptions?: ClassifyErrorOptions;
};

export const QueryErrorState = ({
  error,
  onRetry,
  showBack = true,
  showSupportLink = true,
  expectedCodeErrors,
  onAction,
  actionLabel,
  classifyOptions,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const classified = useMemo(
    () =>
      classifyError(error, {
        ...classifyOptions,
        layer: 'fetch',
        expectedCodeErrors,
      }),
    [error, expectedCodeErrors, classifyOptions]
  );
  const { title, description } = classified.userMessage;
  const isExpected = classified.expectation === 'expected';

  const handleBack = (): void => {
    navigate(-1);
  };

  return (
    <VStack gap="l" px="l" py="4xl" justifyContent="center" alignItems="center" width="100%">
      <Image src={ErrorImage} />
      <Text textStyle="h1.medium" textAlign="center" color="text.primary">
        {title}
      </Text>
      <Text textAlign="center" textStyle="h4.regular" color="text.secondary">
        {description}
      </Text>
      {onAction && actionLabel && (
        <Button onClick={onAction} type="button" variant="primary" w="full" maxWidth="350px">
          {actionLabel}
        </Button>
      )}
      {onRetry && (
        <Button
          onClick={onRetry}
          type="button"
          variant={isExpected ? 'secondaryOutline' : 'primary'}
          w="full"
          maxWidth="350px"
        >
          Erneut versuchen
        </Button>
      )}
      {showBack && (
        <Button
          onClick={handleBack}
          type="button"
          variant="secondaryOutline"
          w="full"
          maxWidth="350px"
        >
          Zurück
        </Button>
      )}
      {showSupportLink && !isExpected && (
        <Button type="button" variant="tertiary" w="full" maxWidth="350px">
          <Link href={ExternalRoutes.HELP} target="_blank" rel="noreferrer noopener">
            Support kontaktieren
          </Link>
        </Button>
      )}
    </VStack>
  );
};
